"use client";

import React, { useEffect, useState } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import styles from "./cart.module.scss";
import { Cart, CartItem } from "@/common/entities/cart/CartItem";
import LocalStorageService from "@/services/localStorageService/LocalStorageService";
import { LocalStorageKeys } from "@/services/localStorageService/LocalStorageKeys";
import CartItemRow from "@/components/Cart/CartItemRow/CartItemRow";
import { FaAngleLeft } from "react-icons/fa";
import PriceDisplay from "@/components/PriceDisplay/PriceDisplay";
import Link from "next/link";
import CartNoProducts from "./CartNoProducts";
import { deleteConfiguratorCartProductsFromCart, deleteProductCart, updateCartProducts } from "@/services/CartService";
import toast from "react-hot-toast";
import useDebounce from "@/common/hooks/useDebounce";
import { Configurators } from "@/common/entities/configurators/Configurator";
import { generateFullCheckoutCart } from "@/common/helpers/util.functions";
import ConfiguratorItemRow from "@/components/Cart/CartItemRow/ConfiguratorItemRow";
import GTMEventTracker from "@/components/GTMEventTracker/GTMEventTracker";
import { pushToDataLayer } from "@/common/utils/GtmUtils";

export interface CheckoutCartConfigurator {
    configurator: Configurators;
    cartProducts: CartItem[];
    configuratorName: string;
    configuratorCode: string;
}

export interface CheckoutCart extends Cart {
    configurators: CheckoutCartConfigurator[];
}

const CartForm = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [cart, setCart] = useState<CheckoutCart>();

    useEffect(() => {
        const cart = LocalStorageService.getItem(LocalStorageKeys.cart);
        if (cart) {
            setCart(generateFullCheckoutCart(cart));
        }
        setIsLoading(false);
    }, []);

    const updateCart = async (cartProductId: number, quantity: number) => {
        {
            if (cart) {
                try {
                    const updatedCart = await updateCartProducts(cart.id, {
                        cartProductId: Number(cartProductId),
                        quantity: quantity,
                    });
                    LocalStorageService.setItem(LocalStorageKeys.cart, updatedCart, 3600 * 24 * 30);
                    setCart(generateFullCheckoutCart(updatedCart));
                } catch (error) {
                    console.error("Error updating cart item:", error);
                    LocalStorageService.clear();
                    toast.error("A apărut o eroare la actualizarea coșului. Vă rugăm să încercați din nou.");
                }
            }
        }
    };

    const debouncedFetchData = useDebounce(updateCart, 500);

    const handleItemUpdate = async (cartProductId: number, quantity: number) => {
        await debouncedFetchData(cartProductId, quantity);
    };

    const handleItemDelete = async (cartProductId: number) => {
        if (cart) {
            const deletedItem = cart.cartProducts.find((item) => item.id === cartProductId);
            if (deletedItem) {
                const product = deletedItem.product;
                const quantity = Number(deletedItem.quantity);

                pushToDataLayer("", { ecommerce: null });
                pushToDataLayer("remove_from_cart", {
                    google_business_vertical: "retail",
                    ecommerce: {
                        currency: "RON",
                        value: (product.specialPriceWithVat ?? product.priceWithVat).toString(),
                        items: [
                            {
                                item_id: product.id.toString(),
                                item_name: product.name,
                                price: (product.specialPriceWithVat ?? product.priceWithVat).toString(),
                                price_tax_exc: product.priceWithoutVat.toString(),
                                price_tax_inc: (product.specialPriceWithVat ?? product.priceWithVat).toString(),
                                item_brand: product.brand?.name || "N/A",
                                item_category: product.categories?.[0]?.category?.name || "Nedefinit",
                                quantity,
                                google_business_vertical: "retail",
                            },
                        ],
                    },
                });
            }

            try {
                const updatedCart = await deleteProductCart(cart.id, Number(cartProductId));
                LocalStorageService.setItem(LocalStorageKeys.cart, updatedCart, 3600 * 24 * 30);
                setCart(generateFullCheckoutCart(updatedCart));
                toast.success("Produsul a fost sters din cos.");
            } catch (error) {
                console.error("Error deleting cart item:", error);
                LocalStorageService.clear();
                toast.error("A apărut o eroare la ștergerea produsului din coș. Vă rugăm să încercați din nou.");
            }
        }
    };

    const handleConfiguratorDelete = async (configuratorCode: string) => {
        if (cart) {
            try {
                const updatedCart = await deleteConfiguratorCartProductsFromCart(cart.id, configuratorCode);
                LocalStorageService.setItem(LocalStorageKeys.cart, updatedCart, 3600 * 24 * 30);
                setCart(generateFullCheckoutCart(updatedCart));
                toast.success("Configuratorul a fost sters din cos.");
            } catch (error) {
                console.error("Error deleting configurator from cart:", error);
                LocalStorageService.clear();
                toast.error("A apărut o eroare la ștergerea configuratorului din coș. Vă rugăm să încercați din nou.");
            }
        }
    };

    if (isLoading) {
        return (
            <div className="text-center">
                <Spinner animation="border" />
            </div>
        );
    }

    const gtmViewCartData = cart && {
        pageCategory: "cart",
        userLogged: cart.client?.id ? 1 : 0,
        userId: cart.client?.id?.toString() || `guest_${cart.id}`,
        google_business_vertical: "retail",
        ecommerce: {
            currency: "RON",
            value: cart.totalCostWithVat.toString(),
            item_list_id: "",
            item_list_name: "",
            items: cart.cartProducts.map((item) => ({
                item_id: item.product.id.toString(),
                item_name: item.product.name,
                price: item.specialPriceWithVat?.toString() ?? item.priceWithVat?.toString(),
                price_tax_exc: item.specialPriceWithoutVat?.toString() ?? item.priceWithoutVat?.toString(),
                price_tax_inc: item.specialPriceWithVat?.toString() ?? item.priceWithVat?.toString(),
                item_brand: item.product.brand?.name || "N/A",
                item_category: item.product.categories?.[0]?.category?.name || "Nedefinit",
                quantity: Number(item.quantity),
                google_business_vertical: "retail",
            })),
        },
    };

    if (!cart || (cart?.cartProducts.length === 0 && !cart?.configurators?.length)) {
        return <CartNoProducts />;
    }

    return (
        <Row>
            {cart && <GTMEventTracker event="view_cart" data={gtmViewCartData} />}
            <Col md={9}>
                <div className={styles.cartWrapper}>
                    {cart?.cartProducts &&
                        cart?.cartProducts.map((item, index) => (
                            <CartItemRow key={index} item={item} itemUpdateCallBack={handleItemUpdate} itemDeleteCallBack={handleItemDelete} />
                        ))}
                    {cart?.configurators &&
                        cart?.configurators.map((item, index) => (
                            <ConfiguratorItemRow key={index} item={item} configuratorDeleteCallBack={handleConfiguratorDelete} />
                        ))}
                </div>

                <Row className={`d-none d-md-block ${styles.buttonRow}`}>
                    <Col md={5} className="d-flex align-items-right">
                        <Link href="/" passHref>
                            <Button className={styles.continueShoppingButton}>
                                <FaAngleLeft /> CONTINUA CUMPARATURILE
                            </Button>
                        </Link>
                    </Col>
                </Row>
            </Col>

            <Col xs={12} md={3} className={styles.summaryPanel}>
                <PriceDisplay cart={cart} />

                <Link className="d-block d-md-none" href="/" passHref>
                    <Button className={styles.continueShoppingButton}>
                        <FaAngleLeft /> CONTINUA CUMPARATURILE
                    </Button>
                </Link>

                <Link href={"/checkout"}>
                    <Button className={styles.finalizeButton}>SPRE FINALIZARE COMANDA</Button>
                </Link>
            </Col>
        </Row>
    );
};

export default CartForm;
